import React from "react";
import { RevealOnScroll } from "../shared";
import {
  REVENUE_LOOP_THESIS,
  REVENUE_EQUATION,
  REVENUE_LOOP_STEPS,
} from "./howItWorksContent";

const EquationCell = ({ data, variant }) => {
  const isSpread = variant === "spread";
  const isWholesale = variant === "wholesale";

  return (
    <div
      className={`flex flex-col items-center justify-center text-center px-3 sm:px-5 py-4 sm:py-5 rounded-xl border min-w-0 flex-1 ${
        isSpread
          ? "border-[#00D2FF] bg-[#00D2FF]/10"
          : isWholesale
            ? "border-partner bg-[#080A0F] opacity-80"
            : "border-partner bg-[#11151F]"
      }`}
    >
      <span className="text-[10px] text-partner-muted mb-2">{data.label}</span>
      <span
        className={`font-mono font-bold leading-none ${
          isSpread
            ? "text-2xl sm:text-3xl text-partner-accent"
            : isWholesale
              ? "text-lg sm:text-xl text-partner-muted line-through decoration-partner-muted/50"
              : "text-xl sm:text-2xl text-partner"
        }`}
      >
        {data.range}
      </span>
      <span className="text-[10px] text-partner-muted mt-1">{data.sub}</span>
    </div>
  );
};

const Operator = ({ symbol }) => (
  <div className="flex-shrink-0 w-6 sm:w-8 flex items-center justify-center">
    <span className="text-lg sm:text-xl font-mono text-partner-accent font-bold">{symbol}</span>
  </div>
);

const AgencyRevenueLoopPanel = () => (
  <div className="revenue-ledger relative" data-testid="agency-revenue-loop-panel">
    <div className="relative rounded-2xl overflow-hidden border border-partner bg-[#11151F]">
      <div className="px-5 sm:px-8 py-8 sm:py-10">
        <RevealOnScroll>
          <h3 className="text-2xl sm:text-3xl font-bold text-partner mb-3 leading-tight">
            The Agency <span className="gradient-text">Revenue Loop</span>
          </h3>
          <p className="text-base text-partner-muted font-body leading-relaxed max-w-2xl mb-8">
            {REVENUE_LOOP_THESIS}
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-2 mb-10" data-testid="revenue-equation">
            <EquationCell data={REVENUE_EQUATION.retail} variant="retail" />
            <Operator symbol="−" />
            <EquationCell data={REVENUE_EQUATION.wholesale} variant="wholesale" />
            <Operator symbol="=" />
            <EquationCell data={REVENUE_EQUATION.spread} variant="spread" />
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <ol className="space-y-6 list-none">
              {REVENUE_LOOP_STEPS.map((item, index) => (
                <li key={item.step} className="flex gap-4" data-testid={`revenue-rung-${index}`}>
                  <span className="flex-shrink-0 w-8 h-8 rounded-full border border-[#00D2FF]/40 bg-[#00D2FF]/10 flex items-center justify-center font-mono text-sm font-bold text-partner-accent">
                    {item.step}
                  </span>
                  <div>
                    <h4 className="text-base font-bold text-partner mb-1">{item.title}</h4>
                    <p className="text-sm text-partner-muted font-body leading-relaxed">{item.description}</p>
                  </div>
                </li>
              ))}
            </ol>

            <div className="rounded-xl border border-[#00D2FF]/25 bg-[#080A0F] p-6 sm:p-7">
              <p className="text-3xl sm:text-4xl font-bold font-mono text-partner-accent leading-none mb-1">
                $150–$400+
              </p>
              <p className="text-sm text-partner-muted mb-5">recurring profit / client / month</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-partner-muted">15 clients × $300 markup</span>
                  <span className="font-mono font-semibold text-partner-accent">+$4,500/mo</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-partner-muted">Annualized spread</span>
                  <span className="font-mono font-semibold text-partner">+$54,000/yr</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-partner-muted">New hires required</span>
                  <span className="font-mono font-semibold text-partner-accent">0</span>
                </div>
              </div>
            </div>
          </div>
        </RevealOnScroll>
      </div>
    </div>
  </div>
);

export default AgencyRevenueLoopPanel;
